/**
 * Notifications admin API client.
 *
 * Wraps the assayer-backend `/admin/notifications/*` endpoints with
 * typed fetchers + response shapes. Two endpoint families forward to
 * publishing-service for the Mailjet REST writes:
 *   - approveDraft      → publishing-service runs the per-recipient sends
 *   - addSuppression    → publishing-service syncs to Mailjet contact blocklist
 */

import { apiClient } from './config.js';

// ---------------------------------------------------------------------
// Types — mirror src/services/email/* + database/email.queries.ts on
// the publishing-service side, and src/assayer/database/notification_queries.py
// on the backend.
// ---------------------------------------------------------------------

export type NotificationStatus =
  | 'sent'
  | 'test_sent'
  | 'suppressed'
  | 'excluded'
  | 'excluded_company'
  | 'failed'
  | 'throttled';

export type DraftStatus =
  | 'pending_approval'
  | 'approved'
  | 'scheduled'  // mig 173 — caller-scheduled send pending in pipeline_jobs
  | 'sent'
  | 'cancelled'
  | 'expired'
  | 'rejected';

export type DeliveryStatus =
  | 'delivered'
  | 'bounce'
  | 'blocked'
  | 'spam'
  | 'unsub'
  | 'open'
  | 'click';

export interface DraftRecipient {
  email: string;
  name: string | null;
  role: string;
  confidence: number;
  organisation: string | null;
  context_snippet: string | null;
  verdict: 'included' | 'excluded';
  reason: string | null;
  organisation_anomaly: boolean;
  /**
   * Per-(recipient, entity) trust ledger state from
   * assayer_notification_recipient_review. NULL = pair never seen.
   * 'approved' = trusted (Send button can fire without an approve step).
   * 'pending'  = enqueued but operator hasn't decided.
   * 'rejected' = operator explicitly rejected; soft-locked from re-inclusion
   * via the standard drawer (operator can flip from /notifications/recipient-reviews).
   */
  review_status: 'pending' | 'approved' | 'rejected' | null;
  // ---- 2026-06-05 enrichment additions ----------------------------------
  /** Stable UUID per row. Used as React key and for PATCH/DELETE addressing.
   *  Backfilled server-side on first mutation for legacy drafts. */
  recipient_id?: string;
  /** 'person' (Cynapse contact) or 'entity_channel' (entity_contacts row). */
  kind?: 'person' | 'entity_channel';
  /** 'operator_added' on rows added via the new modal. */
  source?: 'extraction' | 'operator_added';
  /** Source of the email — 'operator_added' when typed in via the pencil edit. */
  email_source?: 'extraction' | 'operator_added';
  /** Cynapse global_entities.entity_id for persons. */
  cynapse_entity_id?: string | null;
  /** entity_contacts.contact_id for entity-channel rows. */
  entity_contact_id?: string;
  /** Company entity for entity-channel rows. */
  entity_id?: string;
  /** entity_contacts.contact_type for entity-channel rows. */
  contact_type?: string;
}

export interface DraftSummary {
  draft_id: string;
  article_id: string;
  target_id: string;
  announcement_id: string;
  rendered_subject: string;
  status: DraftStatus;
  created_at: string;
  expires_at: string;
  reviewed_by: string | null;
  reviewed_at: string | null;
  article_title: string | null;
  target_name: string | null;
  notification_from_email: string | null;
  notification_from_name: string | null;
  included_count: number;
  excluded_count: number;
  /** Live cap projections (recomputed on each list fetch). */
  per_recipient_cap: number;
  per_publication_cap: number | null;
  target_test_mode_enabled: boolean | null;
  sent_today_target: number;
  /** Recipients in this draft (included) whose today's-sent count already hits per_recipient_cap. */
  recipient_cap_hit_count: number;
  recipients?: DraftRecipient[];
  /** Entity display name + canonical identifier (ticker) for the article — drives the card badge. */
  entity_display_name: string | null;
  entity_identifier: string | null;
  /** Sales-relationship badges: coverage | campaign | prospect | major. */
  coverage_tiers?: string[];
  // ---- mig 173 scheduling --------------------------------------------------
  /** When the operator scheduled the send to fire (ISO 8601 UTC). NULL unless status='scheduled'. */
  scheduled_for: string | null;
  /** Operator email/uid who scheduled it (audit_identity). NULL unless scheduled. */
  scheduled_by: string | null;
  /** Published-article URL captured at draft creation. NULL on legacy rows (pre-mig 173). */
  live_url: string | null;
  /** Articles.entity_id — the canonical key for the Grouped view's per-entity bucketing. */
  article_entity_id: string | null;
  /** Article's generation timestamp (ISO 8601 UTC). Live-mode article date per feedback_article_date_resolution. */
  article_generation_timestamp: string | null;
  /** Article's published_at (ISO 8601 UTC). NULL if not yet promoted to a live site. */
  article_published_at: string | null;
}

export interface DraftCapPreview {
  per_recipient_cap: number;
  per_publication_cap: number | null;
  sent_today_target: number;
  /** Lowercased email → count of today's released sends to that address on this target. */
  recipient_counts: Record<string, number>;
}

export interface DraftDetail extends DraftSummary {
  rendered_html: string;
  rendered_text: string;
  recipients: DraftRecipient[];
  bcc_email: string;
  review_notes: string | null;
  edits_applied: Record<string, unknown> | null;
  send_started_at: string | null;
  send_completed_at: string | null;
  published_slug: string | null;
  entity_display_name: string | null;
  entity_identifier: string | null;
  target_config: Record<string, unknown> | null;
  cap_preview: DraftCapPreview;
  /** Operator-editable body text (seeded with the default copy). Drives the
   *  content editor; the locked footer/opt-out are appended at render time. */
  operator_body: string | null;
  /** Optimistic-lock token + last-content-editor audit (migration 182). */
  updated_at: string | null;
  edited_by: string | null;
  edited_at: string | null;
}

export interface EditDraftContentResult {
  ok: boolean;
  updated_at: string | null;
  edited_by: string | null;
  edited_at: string | null;
  rendered_subject: string;
  rendered_html: string;
  rendered_text: string;
  operator_body: string;
}

export interface FinalRecipient {
  email: string;
  included: boolean;
  operator_override?: boolean;
}

export interface ApproveDraftRequest {
  recipients_final: FinalRecipient[];
  subject_override?: string;
  html_override?: string;
  text_override?: string;
  notes?: string;
  force_approve_warnings?: string[];
  test_mode_override?: boolean;
}

export interface ApprovalResult {
  ok: boolean;
  error?: string;
  error_detail?: string;
  draft_id: string;
  sent_count: number;
  test_sent_count: number;
  excluded_count: number;
  failed_count: number;
}

export interface NotificationAuditRow {
  notification_id: string;
  article_id: string;
  announcement_id: string;
  target_id: string;
  recipient_email: string;
  intended_recipient_email: string | null;
  recipient_name: string | null;
  recipient_role: string | null;
  from_email: string;
  reply_to_email: string;
  mailjet_message_id: string | null;
  mailjet_custom_id: string | null;
  status: NotificationStatus;
  reason: string | null;
  error_message: string | null;
  sent_at: string;
  delivery_status: DeliveryStatus | null;
  delivery_event_at: string | null;
}

export interface TargetNotificationConfig {
  target_id: string;
  target_name: string;
  target_type: string;
  is_active: boolean;
  notification_from_email: string | null;
  notification_from_name: string | null;
  config: {
    mailjet_outreach_list_id?: number | null;
    mailjet_subscriber_list_id?: number | null;
    email_branding?: Record<string, string> | null;
    legal?: { legal_name?: string; postal_address?: string; abn?: string } | null;
    notification_daily_cap?: number | null;
    notification_min_confidence?: number | null;
    notification_require_first_touch_review?: boolean | null;
    notification_allowlist_mode?: boolean | null;
    test_mode_enabled?: boolean | null;
    test_mode_recipient?: string | null;
    test_mode_recent_recipients?: string[] | null;
    publish_domain?: string | null;
  } | null;
}

export interface TargetSettingsPatch {
  notification_from_email?: string | null;
  notification_from_name?: string | null;
  config_patch?: {
    email_branding?: Record<string, string>;
    legal?: { legal_name?: string; abn?: string };
    notification_daily_cap?: number;
    notification_min_confidence?: number;
    notification_max_per_recipient_per_day?: number;
    notification_require_first_touch_review?: boolean;
    test_mode_enabled?: boolean;
    test_mode_recipient?: string;
    mailjet_subscriber_list_id?: number;
  };
}

export interface SuppressionRow {
  email: string;
  scope: string;
  suppressed_at: string;
  reason: string;
  source: string | null;
  suppressed_by: string | null;
  notes: string | null;
}

export interface CompanyExclusionRow {
  entity_id: string;
  excluded_by: string;
  excluded_at: string;
  reason: string | null;
  notes: string | null;
  display_name: string | null;
  canonical_identifier: string | null;
}

export type RecipientReviewStatus = 'pending' | 'approved' | 'rejected';

export interface RecipientReviewRow {
  review_id: string;
  recipient_email: string;
  entity_id: string;
  first_seen_article_id: string | null;
  first_seen_article_title: string | null;
  status: RecipientReviewStatus;
  reviewed_by: string | null;
  reviewed_at: string | null;
  notes: string | null;
  created_at: string;
  entity_display_name: string | null;
  entity_identifier: string | null;
  /**
   * Per-recipient profile enriched from the most recent
   * announcement_extracted_contacts row containing this email.
   * Often the contact_organization is a PR firm or external affiliation
   * distinct from the entity_display_name (which is the announcing
   * company). Both are useful — entity is the trust scope; organisation
   * is who the recipient actually works for.
   */
  contact_name: string | null;
  contact_title: string | null;
  contact_organization: string | null;
  contact_role: string | null;
}

export interface AllowlistEntry {
  recipient_email: string;
  added_by: string;
  added_at: string;
  notes: string | null;
}

// ---------------------------------------------------------------------
// Fetchers
// ---------------------------------------------------------------------

const BASE = '/admin/notifications';

// System + targets -----------------------------------------------------
export interface SystemConfig {
  notifications_enabled: boolean;
  description: string;
}

export interface TargetOverview {
  target_id: string;
  target_name: string;
  target_type: string;
  is_active: boolean;
  notification_from_email: string | null;
  notification_from_name: string | null;
  config: Record<string, unknown> | null;
  pending_draft_count: number;
  sent_today_count: number;
  test_sent_today_count: number;
}

export async function getSystemConfig(): Promise<SystemConfig> {
  const r = await apiClient.get(`${BASE}/system-config`);
  return r.data;
}

export async function setSystemEnabled(enabled: boolean): Promise<SystemConfig> {
  const r = await apiClient.put(`${BASE}/system-config`, { enabled });
  return r.data;
}

export async function listTargets(): Promise<{ items: TargetOverview[] }> {
  const r = await apiClient.get(`${BASE}/targets`);
  return r.data;
}

export async function setTargetEnabled(
  targetId: string,
  enabled: boolean,
): Promise<{ target_id: string; target_name: string; config: Record<string, unknown> }> {
  const r = await apiClient.put(`${BASE}/targets/${targetId}/enabled`, { enabled });
  return r.data;
}

// Drafts -----------------------------------------------------------------
export interface ListQueueParams {
  /** Draft status filter. Pass 'all' to disable status filtering (e.g. for search). */
  status?: DraftStatus | 'all';
  target_id?: string;
  /** YYYY-MM-DD — drafts created on this calendar date. */
  date?: string;
  /** Free-text search across article title, entity name, ticker, subject. */
  q?: string;
  /** Entity-group filter pill — see EntityGroupFilter.tsx. */
  entity_group?: 'coverage' | 'prospects' | 'majors' | 'picked' | 'all' | 'campaigns';
  limit?: number;
  offset?: number;
}

export async function listQueue(
  params: ListQueueParams = {},
): Promise<{ items: DraftSummary[]; count: number; limit: number; offset: number }> {
  const r = await apiClient.get(`${BASE}/queue`, { params });
  return r.data;
}

export async function getDraft(draftId: string): Promise<DraftDetail> {
  const r = await apiClient.get(`${BASE}/drafts/${draftId}`);
  return r.data;
}

/**
 * Persist an operator subject + body edit to a draft before send. Optimistic-
 * locked on a CONTENT SNAPSHOT: pass the subject/body the editor originally
 * loaded as `expected`; a 409 (routed through handleMutationConflict) fires only
 * when the stored content differs from that base — i.e. another operator changed
 * it. (We deliberately do NOT lock on updated_at: the draft row is rewritten
 * constantly by background refreshes that bump updated_at without changing the
 * content, which produced false conflicts on nearly every save.) The body is
 * re-rendered server-side into the locked compliance shell.
 */
export async function updateDraftContent(
  draftId: string,
  edit: { subject: string; body: string },
  expected: { subject: string; body: string },
): Promise<EditDraftContentResult> {
  const r = await apiClient.patch(`${BASE}/drafts/${draftId}/content`, {
    subject: edit.subject,
    body: edit.body,
    expected_subject: expected.subject,
    expected_body: expected.body,
  });
  return r.data;
}

/**
 * @deprecated Use approveAndSendDraft. Kept as a thin alias for older
 * components during the rollout window.
 */
export async function approveDraft(
  draftId: string,
  body: ApproveDraftRequest,
): Promise<ApprovalResult> {
  return approveAndSendDraft(draftId, body);
}

/** Approve recipients (ledger flip to 'approved') AND send the email in one click. */
export async function approveAndSendDraft(
  draftId: string,
  body: ApproveDraftRequest,
): Promise<ApprovalResult> {
  const r = await apiClient.post(`${BASE}/drafts/${draftId}/approve-and-send`, body);
  return r.data;
}

/** Result of approve-recipients (ledger write only, no Mailjet send). */
export interface ApproveRecipientsResult {
  ok: boolean;
  error?: string;
  error_detail?: string;
  draft_id: string;
  approved_count: number;
  newly_approved_count: number;
}

/** Approve a list of recipients as trusted (recipient, entity) pairs. Draft stays pending; no email sent. */
export async function approveRecipients(
  draftId: string,
  recipientEmails: string[],
): Promise<ApproveRecipientsResult> {
  const r = await apiClient.post(`${BASE}/drafts/${draftId}/approve-recipients`, {
    recipient_emails: recipientEmails,
  });
  return r.data;
}

/** Send draft email — all included recipients must already be approved in the ledger. */
export async function sendDraft(
  draftId: string,
  body: ApproveDraftRequest,
): Promise<ApprovalResult> {
  const r = await apiClient.post(`${BASE}/drafts/${draftId}/send`, body);
  return r.data;
}

export async function rejectDraft(
  draftId: string,
  notes?: string,
): Promise<{ ok: boolean }> {
  const r = await apiClient.post(`${BASE}/drafts/${draftId}/reject`, { notes });
  return r.data;
}

export async function cancelDraft(
  draftId: string,
  notes?: string,
): Promise<{ ok: boolean }> {
  const r = await apiClient.post(`${BASE}/drafts/${draftId}/cancel`, { notes });
  return r.data;
}

// ---------------------------------------------------------------------
// Entity-day grouping + scheduling (mig 173 / NOTIFICATIONS_ENTITY_DAY_SCHEDULING_PLAN)
// ---------------------------------------------------------------------

export interface EntityDayGroupRow {
  entity_id: string | null;            // null for the sitewide / synthesis bucket
  entity_display_name: string | null;  // 'Sitewide / synthesis' for the null bucket
  entity_identifier: string | null;    // ticker / canonical id
  /** Sales-relationship badges for the group's company: coverage | campaign | prospect | major. */
  coverage_tiers?: string[];
  drafts: DraftSummary[];
  /** target_names where a draft for this (entity, day) is status='sent'.
   *  Powers the per-publication "already sent" badges on the group header. */
  sent_targets: string[];
  /** target_names where a draft for this (entity, day) is status='scheduled'.
   *  Powers the grey "queued for {pub}" badges on the group header. */
  scheduled_targets: string[];
}

export async function listQueueByEntityDay(
  params: ListQueueParams = {},
): Promise<{ groups: EntityDayGroupRow[]; count: number }> {
  const r = await apiClient.get(`${BASE}/queue/by-entity-day`, { params });
  return r.data;
}

export interface ListScheduledParams {
  /** YYYY-MM-DD Sydney. Defaults to current week Monday on server. */
  from?: string;
  /** YYYY-MM-DD Sydney. Defaults to current week Sunday on server. */
  to?: string;
  target_id?: string;
  entity_group?: 'coverage' | 'prospects' | 'majors' | 'picked' | 'all' | 'campaigns';
  q?: string;
}

export async function listScheduled(
  params: ListScheduledParams = {},
): Promise<{ items: DraftSummary[]; count: number; from: string; to: string }> {
  const r = await apiClient.get(`${BASE}/scheduled`, { params });
  return r.data;
}

export interface ScheduleDraftRequest {
  recipients_final: FinalRecipient[];
  /** ISO 8601 UTC timestamp. Must be strictly before the draft's expires_at. */
  scheduled_for: string;
  force_approve_warnings?: string[];
  test_mode_override?: boolean;
  notes?: string;
}

/**
 * Schedule a future send. Approves recipients in the trust ledger AND queues
 * a pipeline_jobs row that fires at `scheduled_for` via the worker.
 * Subject / body overrides at schedule time are NOT supported in v1 —
 * use the draft editor to update copy BEFORE scheduling.
 */
export async function scheduleDraft(
  draftId: string,
  body: ScheduleDraftRequest,
): Promise<{ ok: boolean; draft: DraftDetail }> {
  const r = await apiClient.post(`${BASE}/drafts/${draftId}/schedule`, body);
  return r.data;
}

/**
 * Remove a scheduled send. Draft moves back to 'approved' (recipient-trust-
 * ledger approval is preserved). Distinct from cancelDraft (terminal-kill).
 */
export async function unscheduleDraft(draftId: string): Promise<{ ok: boolean }> {
  const r = await apiClient.post(`${BASE}/drafts/${draftId}/unschedule`, {});
  return r.data;
}

// Article notifications + exclusions -----------------------------------
export async function listArticleNotifications(
  articleId: string,
): Promise<{ items: NotificationAuditRow[] }> {
  const r = await apiClient.get(`${BASE}/articles/${articleId}/notifications`);
  return r.data;
}

export async function listArticleExclusions(
  articleId: string,
): Promise<{ items: Array<{ recipient_email: string; excluded_by: string; excluded_at: string; reason: string | null }> }> {
  const r = await apiClient.get(`${BASE}/articles/${articleId}/exclusions`);
  return r.data;
}

export async function addArticleExclusion(
  articleId: string,
  email: string,
  reason?: string,
): Promise<{ ok: boolean }> {
  const r = await apiClient.post(`${BASE}/articles/${articleId}/exclusions`, {
    recipient_email: email, reason,
  });
  return r.data;
}

export async function removeArticleExclusion(
  articleId: string,
  email: string,
): Promise<{ ok: boolean }> {
  const r = await apiClient.delete(`${BASE}/articles/${articleId}/exclusions/${encodeURIComponent(email)}`);
  return r.data;
}

// Entity config + exclusion --------------------------------------------
export async function getEntityConfig(
  entityId: string,
): Promise<{ entity_id: string; per_article_enabled: boolean; updated_by: string | null; updated_at: string | null; is_default: boolean }> {
  const r = await apiClient.get(`${BASE}/entities/${entityId}/config`);
  return r.data;
}

export async function updateEntityConfig(
  entityId: string,
  perArticleEnabled: boolean,
): Promise<{ ok: boolean }> {
  const r = await apiClient.put(`${BASE}/entities/${entityId}/config`, {
    per_article_enabled: perArticleEnabled,
  });
  return r.data;
}

export async function getCompanyExclusion(
  entityId: string,
): Promise<CompanyExclusionRow | null> {
  try {
    const r = await apiClient.get(`${BASE}/entities/${entityId}/exclusion`);
    return r.data;
  } catch (e: unknown) {
    const err = e as { response?: { status?: number } };
    if (err.response?.status === 404) return null;
    throw e;
  }
}

export async function upsertCompanyExclusion(
  entityId: string,
  reason?: string,
  notes?: string,
): Promise<{ ok: boolean }> {
  const r = await apiClient.put(`${BASE}/entities/${entityId}/exclusion`, { reason, notes });
  return r.data;
}

export async function removeCompanyExclusion(
  entityId: string,
): Promise<{ ok: boolean }> {
  const r = await apiClient.delete(`${BASE}/entities/${entityId}/exclusion`);
  return r.data;
}

export async function listCompanyExclusions(
  params: { limit?: number; offset?: number } = {},
): Promise<{ items: CompanyExclusionRow[] }> {
  const r = await apiClient.get(`${BASE}/company-exclusions`, { params });
  return r.data;
}

// Suppressions ----------------------------------------------------------
export async function listSuppressions(
  params: { scope?: string; reason?: string; limit?: number; offset?: number } = {},
): Promise<{ items: SuppressionRow[] }> {
  const r = await apiClient.get(`${BASE}/suppressions`, { params });
  return r.data;
}

export async function addSuppression(
  email: string,
  scope: 'global' | string,
  reason: 'manual' | 'unsubscribe' | 'bounce' | 'complaint' = 'manual',
  notes?: string,
): Promise<{ ok: boolean; mailjetOk?: boolean; mailjetError?: string }> {
  const r = await apiClient.post(`${BASE}/suppressions`, { email, scope, reason, notes });
  return r.data;
}

export async function removeSuppression(
  email: string,
  scope: string,
): Promise<{ ok: boolean }> {
  const r = await apiClient.delete(`${BASE}/suppressions/${encodeURIComponent(email)}`, {
    params: { scope },
  });
  return r.data;
}

// Recipient reviews (Gate 6) — cross-article trust ledger ---------------
export interface ListRecipientReviewsParams {
  /** 'pending' (default) | 'approved' | 'rejected' | 'all'. */
  status?: RecipientReviewStatus | 'all';
  /** Free-text across recipient_email, entity display_name, ticker. */
  q?: string;
  limit?: number;
  offset?: number;
}

export async function listRecipientReviews(
  params: ListRecipientReviewsParams = {},
): Promise<{ items: RecipientReviewRow[]; count: number }> {
  const r = await apiClient.get(`${BASE}/recipient-reviews`, { params });
  return r.data;
}

export async function approveRecipientReview(
  reviewId: string,
  notes?: string,
): Promise<{ ok: boolean }> {
  const r = await apiClient.post(`${BASE}/recipient-reviews/${reviewId}/approve`, { notes });
  return r.data;
}

export async function rejectRecipientReview(
  reviewId: string,
  notes?: string,
): Promise<{ ok: boolean }> {
  const r = await apiClient.post(`${BASE}/recipient-reviews/${reviewId}/reject`, { notes });
  return r.data;
}

export interface BulkReviewResult {
  ok: boolean;
  requested: number;
  updated: number;
  not_pending: number;
}

/** Atomically approve up to 500 (recipient, entity) review pairs in one
 *  transaction. Replaces the previous client-side per-row loop —
 *  navigate-away during the HTTP call cannot leave the ledger half-
 *  written, and the response carries counts for a clean operator
 *  summary ("approved 60, 5 already approved"). */
export async function bulkApproveRecipientReviews(
  reviewIds: string[],
  notes?: string,
): Promise<BulkReviewResult> {
  const r = await apiClient.post(`${BASE}/recipient-reviews/bulk-approve`, {
    review_ids: reviewIds,
    notes,
  });
  return r.data;
}

export async function bulkRejectRecipientReviews(
  reviewIds: string[],
  notes?: string,
): Promise<BulkReviewResult> {
  const r = await apiClient.post(`${BASE}/recipient-reviews/bulk-reject`, {
    review_ids: reviewIds,
    notes,
  });
  return r.data;
}

// Per-target allowlist + settings ---------------------------------------
export async function listTargetAllowlist(targetId: string): Promise<{ items: AllowlistEntry[] }> {
  const r = await apiClient.get(`${BASE}/targets/${targetId}/allowlist`);
  return r.data;
}

export async function addToTargetAllowlist(
  targetId: string,
  email: string,
  notes?: string,
): Promise<{ ok: boolean }> {
  const r = await apiClient.post(`${BASE}/targets/${targetId}/allowlist`, {
    recipient_email: email, notes,
  });
  return r.data;
}

export async function removeFromTargetAllowlist(
  targetId: string,
  email: string,
): Promise<{ ok: boolean }> {
  const r = await apiClient.delete(
    `${BASE}/targets/${targetId}/allowlist/${encodeURIComponent(email)}`,
  );
  return r.data;
}

export async function getTargetNotificationSettings(
  targetId: string,
): Promise<TargetNotificationConfig> {
  const r = await apiClient.get(`${BASE}/targets/${targetId}/settings`);
  return r.data;
}

export async function updateTargetNotificationSettings(
  targetId: string,
  patch: TargetSettingsPatch,
): Promise<TargetNotificationConfig> {
  const r = await apiClient.put(`${BASE}/targets/${targetId}/settings`, patch);
  return r.data;
}

// ---------------------------------------------------------------------
// Client-side helpers — soft vs hard lock rules for recipient
// re-inclusion (must match the publishing-service notification-sender.ts
// SOFT/HARD_LOCKED_REASON_PREFIXES exactly).
// ---------------------------------------------------------------------

const SOFT_LOCKED_PREFIXES = [
  'low_confidence:',
  'disposable_domain',
  'role_not_in_scope:',
  'pending_first_touch_review',
  'first_touch_rejected',
  'operator_removed',
];

const HARD_LOCKED_PREFIXES = [
  'suppressed:',
  'excluded_company',
  'no_mx',
];

export function isSoftLocked(reason: string | null | undefined): boolean {
  if (!reason) return false;
  return SOFT_LOCKED_PREFIXES.some(p =>
    p.endsWith(':') ? reason.startsWith(p) : reason === p,
  );
}

export function isHardLocked(reason: string | null | undefined): boolean {
  if (!reason) return false;
  return HARD_LOCKED_PREFIXES.some(p =>
    p.endsWith(':') ? reason.startsWith(p) : reason === p,
  );
}

export function explainReason(reason: string | null | undefined): string {
  if (!reason) return 'no reason';
  if (reason === 'included') return 'eligible';
  if (reason.startsWith('low_confidence:')) {
    const v = reason.slice('low_confidence:'.length);
    return `low confidence (${v})`;
  }
  if (reason.startsWith('role_not_in_scope:')) {
    const r = reason.slice('role_not_in_scope:'.length);
    return `role not in scope (${r})`;
  }
  if (reason.startsWith('suppressed:')) {
    const s = reason.slice('suppressed:'.length);
    return s === 'global' ? 'suppressed (account-wide)' : `suppressed (${s})`;
  }
  if (reason === 'excluded_company') return 'company excluded';
  if (reason === 'no_mx') return 'no MX record';
  if (reason === 'invalid_email_syntax') return 'invalid email syntax';
  if (reason === 'disposable_domain') return 'disposable email domain';
  if (reason === 'article_excluded') return 'excluded for this article';
  if (reason.startsWith('recipient_cap:')) {
    const n = reason.slice('recipient_cap:'.length);
    return `daily cap reached for this recipient (${n}/day)`;
  }
  if (reason === 'pending_first_touch_review') return 'awaiting first-touch review';
  if (reason === 'first_touch_rejected') return 'rejected at first-touch review';
  if (reason === 'entity_disabled') return 'entity notifications disabled';
  if (reason === 'no_email_address') return 'contact has no email';
  if (reason === 'operator_removed') return 'removed by operator';
  if (reason.startsWith('operator_override:')) {
    return `operator override (was ${reason.slice('operator_override:'.length)})`;
  }
  if (reason.startsWith('throttled:')) return 'daily cap exceeded';
  if (reason === 'operator_added') return 'added by operator';
  return reason;
}

// =====================================================================
// Operator-side recipient enrichment (2026-06-05).
// Add / edit / delete recipients on a notification draft.
// =====================================================================

export type PersonRoleEnum =
  | 'media_contact'
  | 'pr_representative'
  | 'investor_relations'
  | 'analyst'
  | 'executive'
  | 'board_member'
  | 'advisor';

export type EntityChannelTypeEnum =
  | 'media'
  | 'investor_relations'
  | 'executive_office'
  | 'general'
  | 'generic_inbox';

export interface EditRecipientRequest {
  email?: string;
  name?: string;
  role?: PersonRoleEnum;
}

export interface PatchRecipientResult {
  recipient: DraftRecipient;
  diff: Record<string, { old: unknown; new: unknown }>;
  cynapse_sync: {
    called: boolean;
    entity_id?: string | null;
    was_merged?: boolean;
    error?: string;
  };
  announcement_extracted_contacts_patch: {
    patched: boolean;
    ambiguous?: boolean;
    contact_count_matched?: number;
    error?: string;
  };
}

export async function patchRecipient(
  draftId: string,
  recipientId: string,
  body: EditRecipientRequest,
): Promise<PatchRecipientResult> {
  const r = await apiClient.patch(`${BASE}/drafts/${draftId}/recipients/${recipientId}`, body);
  return r.data;
}

export interface AddPersonRecipientRequest {
  kind: 'person';
  full_name: string;
  role: PersonRoleEnum;
  organisation?: string;
  email: string;
  phone?: string;
  /** After operator picks "use existing" on a fuzzy candidate. */
  cynapse_entity_id?: string;
  /** After operator picks "create new anyway" on a fuzzy candidate. */
  force_new?: boolean;
}

export interface AddEntityChannelRequest {
  kind: 'entity_channel';
  entity_id?: string;
  contact_type: EntityChannelTypeEnum;
  channel_email?: string;
  channel_phone?: string;
  contact_name_raw?: string;
  role_label?: string;
}

export interface FuzzyCandidate {
  entity_id: string | null;
  full_name: string | null;
  similarity_score: number | null;
}

export type AddRecipientResult =
  | {
      status: 'added';
      recipient: DraftRecipient;
      cynapse_sync?: {
        called: boolean;
        entity_id?: string | null;
        was_created?: boolean;
        was_merged?: boolean;
      };
      entity_contact?: Record<string, unknown>;
    }
  | {
      status: 'candidate_review_required';
      candidate: FuzzyCandidate;
      next_actions: {
        use_existing: { method: string; path: string; body_changes: Record<string, unknown> };
        create_new: { method: string; path: string; body_changes: Record<string, unknown> };
      };
    };

export async function addRecipient(
  draftId: string,
  body: AddPersonRecipientRequest | AddEntityChannelRequest,
): Promise<AddRecipientResult> {
  const r = await apiClient.post(`${BASE}/drafts/${draftId}/recipients`, body);
  return r.data;
}

export async function deleteRecipient(
  draftId: string,
  recipientId: string,
): Promise<{ removed_recipient_id: string }> {
  const r = await apiClient.delete(`${BASE}/drafts/${draftId}/recipients/${recipientId}`);
  return r.data;
}

// ---------------------------------------------------------------------
// Reader subscribers (email M3, 2026-10-08) — read-only over the Mailjet
// Subscribers-list mirror. Addresses are masked unless revealed (logged).
// ---------------------------------------------------------------------

export type SignupBasis = 'latest' | 'first';
export type SubscriberDimension = 'page' | 'ctx' | 'ref' | 'utm_source' | 'utm_medium' | 'utm_campaign';

export interface SubscriberListSummary {
  list_id: number;
  target_id: string | null;
  target_name: string | null;
  active: number;
  unsubscribed: number;
  removed: number;
  excluded: number;
  legacy: number;
  /** Active internal/test addresses (e.g. @vyklow.com), excluded from every other figure. */
  internal: number;
  last_synced_at: string | null;
  new_signups: number;
  unknown_context: number;
  series: Array<{ day: string; count: number }>;
  breakdowns: Record<SubscriberDimension, Array<{ value: string; count: number }>>;
}

export interface SubscriberSummary {
  days: number;
  basis: SignupBasis;
  network: { unique_active: number; internal: number; internal_domains: string[] };
  lists: SubscriberListSummary[];
  last_reconcile: {
    started_at: string;
    finished_at: string;
    lists_ok: number;
    lists_failed: number;
    new_suppressions: number;
    mirror_rows: number;
    error: string | null;
  } | null;
}

export interface SubscriberRow {
  email: string;
  /** Internal/test address: shown greyed, never counted. */
  is_internal: boolean;
  subscribed_at: string | null;
  is_unsubscribed: boolean;
  unsubscribed_at: string | null;
  is_excluded_from_campaigns: boolean;
  removed_at: string | null;
  latest_is_legacy: boolean | null;
  latest_signup_at: string | null;
  latest_page: string | null;
  latest_ctx: string | null;
  latest_cv: string | null;
  latest_ref: string | null;
  latest_utm_source: string | null;
  latest_utm_medium: string | null;
  latest_utm_campaign: string | null;
  first_signup_at: string | null;
  first_page: string | null;
  first_ref: string | null;
  first_utm_source: string | null;
  first_utm_campaign: string | null;
}

export async function getSubscriberSummary(days: number, basis: SignupBasis): Promise<SubscriberSummary> {
  const r = await apiClient.get(`${BASE}/subscribers/summary`, { params: { days, basis } });
  return r.data;
}

export async function listSubscribers(listId: number, reveal: boolean): Promise<{ items: SubscriberRow[]; revealed: boolean }> {
  const r = await apiClient.get(`${BASE}/subscribers`, { params: { list_id: listId, reveal, limit: 500 } });
  return r.data;
}

// ---------------------------------------------------------------------
// Weekly reader digest (email M4b, 2026-10-09): build + preview only.
// ---------------------------------------------------------------------

export interface DigestItemEvidence {
  article_id: string;
  masthead: string;
  title: string;
  excerpt: string;
  url: string;
  coverage: boolean;
  company_name: string | null;
  published_at: string;
  views: number;
  linkedin_engagement: number | null;
  rank: number;
  corrected_at: string | null;
}

export interface DigestIssue {
  issue_id: string;
  target_id: string;
  target_name: string;
  kind: string;
  period_start: string;
  period_end: string;
  subject: string | null;
  status: string;
  created_by: string | null;
  built_at: string | null;
  expected_recipients: number | null;
  approved_by: string | null;
  scheduled_for: string | null;
  last_test_sent_at: string | null;
  last_test_sent_to?: string | null;
  error_message: string | null;
  content: {
    send_date: string;
    period_label: string;
    list_id: number;
    candidates_considered: number;
    sections: { top: DigestItemEvidence[]; promoted: DigestItemEvidence[]; trending: DigestItemEvidence[] };
  } | null;
  rendered_html?: string | null;
  rendered_text?: string | null;
}

export async function listDigestIssues(periodEnd?: string): Promise<{ items: DigestIssue[]; next_send_date: string }> {
  const r = await apiClient.get(`${BASE}/digest/issues`, { params: periodEnd ? { period_end: periodEnd } : {} });
  return r.data;
}

export async function getDigestIssue(issueId: string): Promise<DigestIssue> {
  const r = await apiClient.get(`${BASE}/digest/issues/${issueId}`);
  return r.data.issue;
}

export async function buildDigest(sendDate?: string): Promise<unknown> {
  const r = await apiClient.post(`${BASE}/digest/build`, sendDate ? { send_date: sendDate } : {});
  return r.data;
}

// Weekly digest M4c: test send, approve, cancel, go switch.
export async function sendDigestTest(issueId: string, to?: string): Promise<{ draft_id: number; changes: string[] }> {
  const r = await apiClient.post(`${BASE}/digest/issues/${issueId}/test`, to ? { to } : {});
  return r.data;
}

export async function approveDigest(issueId: string, builtAt: string): Promise<{ scheduled_for: string; expected_recipients: number; mailjet_active: number }> {
  const r = await apiClient.post(`${BASE}/digest/issues/${issueId}/approve`, { built_at: builtAt });
  return r.data;
}

export async function cancelDigest(issueId: string): Promise<void> {
  await apiClient.post(`${BASE}/digest/issues/${issueId}/cancel`, {});
}

export async function getDigestSettings(): Promise<{ send_enabled: boolean; updated_at: string | null; updated_by: string | null }> {
  const r = await apiClient.get(`${BASE}/digest/settings`);
  return r.data;
}

export async function setDigestSendEnabled(enabled: boolean): Promise<void> {
  await apiClient.put(`${BASE}/digest/settings`, { send_enabled: enabled });
}
